import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { adminApi } from '@/lib/api';
import { AdminModal, SearchBox, formatDate, DetailRow, EmptyState, TableSkeleton } from './AdminShared';
import { Trash2, Loader2 } from 'lucide-react';

export function AdminAgencies() {
  const { getToken } = useAuth();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-agencies', search],
    queryFn: async () => {
      const token = await getToken();
      return adminApi.listAgencies(token!, { search: search || undefined });
    },
  });

  const agencies = data?.agencies ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SearchBox value={search} onChange={setSearch} placeholder="Search agencies…" />
        <span className="text-xs text-muted-foreground">{agencies.length} shown</span>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : agencies.length === 0 ? (
        <EmptyState message="No agencies found" />
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-left">
                <th className="px-4 py-2.5 font-medium">Agency</th>
                <th className="px-4 py-2.5 font-medium">Owner</th>
                <th className="px-4 py-2.5 font-medium text-center">Workspaces</th>
                <th className="px-4 py-2.5 font-medium text-center">Members</th>
                <th className="px-4 py-2.5 font-medium">Created</th>
                <th className="px-4 py-2.5 font-medium w-8" />
              </tr>
            </thead>
            <tbody>
              {agencies.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => setSelectedId(a.id)}
                  className="border-b border-border/60 last:border-0 hover:bg-muted/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2.5 font-medium text-foreground">{a.name}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{a.owner_email ?? '—'}</td>
                  <td className="px-4 py-2.5 text-center text-muted-foreground">{a.workspace_count}</td>
                  <td className="px-4 py-2.5 text-center text-muted-foreground">{a.member_count}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{formatDate(a.created_at)}</td>
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <DeleteAgencyButton id={a.id} name={a.name} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && <AgencyDetail id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function DeleteAgencyButton({ id, name }: { id: string; name: string }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const del = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return adminApi.deleteAgency(token!, id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-agencies'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      setConfirming(false);
    },
  });

  if (confirming) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => del.mutate()}
          disabled={del.isPending}
          className="px-2 py-1 text-[10px] bg-destructive text-destructive-foreground rounded hover:bg-destructive/90 disabled:opacity-50 flex items-center gap-1"
        >
          {del.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
          Confirm
        </button>
        <button
          onClick={() => setConfirming(false)}
          className="px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground rounded hover:bg-muted/40"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      title={`Delete agency "${name}" and all its workspaces`}
      className="p-1.5 text-muted-foreground hover:text-destructive rounded hover:bg-destructive/10 transition-colors"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

function AgencyDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { getToken } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-agency', id],
    queryFn: async () => {
      const token = await getToken();
      return adminApi.getAgency(token!, id);
    },
  });

  return (
    <AdminModal title={data?.agency.name ?? 'Agency'} onClose={onClose} wide>
      {isLoading || !data ? (
        <TableSkeleton rows={4} />
      ) : (
        <div className="space-y-5">
          <div className="border border-border rounded-lg p-3">
            <DetailRow label="Owner">{data.agency.owner_name ?? data.agency.owner_email ?? '—'}</DetailRow>
            <DetailRow label="Owner email">{data.agency.owner_email ?? '—'}</DetailRow>
            <DetailRow label="Agency ID">
              <code className="text-[11px]">{data.agency.id}</code>
            </DetailRow>
            <DetailRow label="Created">{formatDate(data.agency.created_at)}</DetailRow>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2">Members ({data.members.length})</h4>
            <div className="space-y-1">
              {data.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between text-xs bg-muted/40 rounded-md px-3 py-2">
                  <span className="text-foreground truncate">{m.name ?? m.email}</span>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {m.role}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2">Workspaces ({data.workspaces.length})</h4>
            {data.workspaces.length === 0 ? (
              <p className="text-xs text-muted-foreground">No workspaces</p>
            ) : (
              <div className="space-y-1">
                {data.workspaces.map((w) => (
                  <div key={w.id} className="flex items-center justify-between text-xs bg-muted/40 rounded-md px-3 py-2">
                    <span className="text-foreground truncate">{w.name}</span>
                    <span className="text-muted-foreground">{formatDate(w.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminModal>
  );
}
